import { z } from "zod";
import { isReservedSubdomain } from "@profolio/security";
import { safeHttpUrlSchema } from "./safe-url";
import { sanitizeTitle } from "./sanitize";

// ─── Auth Validators ─────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const registerSchema = z
  .object({
    email: z.string().email("Invalid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password too long"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const resetPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export const newPasswordSchema = z
  .object({
    token: z.string().min(1),
    password: z.string().min(8).max(128),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

// ─── Profile Validators ─────────────────────────────────────────────────────

export const subdomainSchema = z
  .string()
  .min(3, "Subdomain must be at least 3 characters")
  .max(63, "Subdomain too long")
  .regex(
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
    "Subdomain must be lowercase, alphanumeric, and may contain hyphens (not at start or end)"
  )
  .refine((val) => !isReservedSubdomain(val), {
    message: "This subdomain is reserved by the platform",
  });

export const colorPalettesEnum = z.enum([
  "ocean-blue",
  "oxford-navy",
  "emerald-forest",
  "royal-amethyst",
  "crimson-slate",
  "amber-gold",
  "midnight-dark",
  "minimal-monochrome",
  "aurora",
  "coastal",
  "saffron",
  "graphite-luxe",
  "botanical",
  "academic-royal",
  "studio-light",
  "neon-minimal",
  "obsidian-executive",
  "sapphire-scholar",
  "cyber-teal",
]);

export const themesEnum = z.enum([
  "classic-academic",
  "modern-executive",
  "minimalist-scholar",
  "grid-showcase",
  "modern-bento",
]);

export const fontPairingsEnum = z.enum([
  "serif-classic",
  "modern-sans",
  "editorial",
  "technical-mono",
  "technical",
  "minimal",
  "nordic-clean",
  "heritage-press",
  "cyber-modern",
]);

export const backgroundStyleEnum = z.enum([
  "solid",
  "gradient-linear",
  "gradient-radial",
  "mesh",
  "animated-gradient",
  "geometric-pattern",
  "particles",
]);

export const headerStyleEnum = z.enum([
  "standard",
  "hero-banner",
  "split-hero",
  "full-bleed",
  "minimal",
  "split-left",
  "split-right",
  "banner",
  "card",
]);

export const animationLevelEnum = z.enum([
  "none",
  "subtle",
  "moderate",
  "dramatic",
]);

export const layoutDensityEnum = z.enum([
  "compact",
  "standard",
  "relaxed"
]);

export const cornerRadiusEnum = z.enum([
  "none",
  "subtle",
  "rounded"
]);

export const sectionArrangementEnum = z.enum([
  "linear",
  "masonry",
  "bento"
]);

export const typographyScaleEnum = z.enum([
  "compact",
  "editorial",
  "display"
]);

export const styleRecipesEnum = z.enum([
  "obsidian-executive",
  "sapphire-scholar",
  "porcelain-swiss",
  "cyber-research",
  "editorial-atelier",
  "aurora-showcase",
]);

export type ThemeKey = z.infer<typeof themesEnum>;
export type ColorPaletteKey = z.infer<typeof colorPalettesEnum>;
export type FontPairing = z.infer<typeof fontPairingsEnum>;
export type BackgroundStyle = z.infer<typeof backgroundStyleEnum>;
export type HeaderStyle = z.infer<typeof headerStyleEnum>;
export type AnimationLevel = z.infer<typeof animationLevelEnum>;
export type LayoutDensity = z.infer<typeof layoutDensityEnum>;
export type CornerRadius = z.infer<typeof cornerRadiusEnum>;
export type SectionArrangement = z.infer<typeof sectionArrangementEnum>;
export type TypographyScale = z.infer<typeof typographyScaleEnum>;
export type StyleRecipeId = z.infer<typeof styleRecipesEnum>;

export const sectionTypeEnum = z.enum([
  "about",
  "research",
  "teaching",
  "projects",
  "publications",
  "experience",
  "contact",
  "skills",
  "downloads",
  "media",
]);

// ─── Pro Feature Schemas ─────────────────────────────────────────────────────

export const sectionOverrideSchema = z.object({
  headerVisible: z.boolean().optional(),
  customTitle: z.string().max(100).transform(v => sanitizeTitle(v)).optional(),
  layoutVariant: z.enum(["default", "split", "grid", "timeline", "showcase"]).optional(),
  spacingBefore: z.enum(["none", "sm", "md", "lg", "xl"]).optional(),
  spacingAfter: z.enum(["none", "sm", "md", "lg", "xl"]).optional(),
  cardStyle: z.enum(["flat", "elevated", "bordered", "glass"]).optional(),
  // ── NEW: Element-level controls ──
  sectionBorder: z.enum(["none", "top", "bottom", "both", "full"]).optional(),
  sectionBorderColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional(),
  sectionBackground: z.enum(["transparent", "elevated", "tinted", "glass", "custom"]).optional(),
  sectionBackgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional(),
  sectionPadding: z.enum(["none", "sm", "md", "lg", "xl"]).optional(),
  contentAlign: z.enum(["left", "center"]).optional(),
  headerSize: z.enum(["sm", "md", "lg"]).optional(),
  headerStyle: z.enum(["default", "underlined", "accent-left", "pill", "hidden", "centered"]).optional(),
  itemSpacing: z.enum(["tight", "normal", "relaxed"]).optional(),
  // ── Section Background & Dynamics ──
  sectionBackgroundImageUrl: z.string().url().max(1024).optional().or(z.literal("")).nullable(),
  sectionBackgroundOverlay: z.string().max(30).optional().nullable(),
  sectionBackgroundPosition: z.enum([
    "top-left", "top-center", "top-right",
    "center-left", "center", "center-right",
    "bottom-left", "bottom-center", "bottom-right",
    "top", "bottom", "cover", "contain", "fixed"
  ]).optional().nullable(),
  sectionBackgroundFit: z.enum(["cover", "contain", "auto", "repeat"]).optional().nullable(),
  sectionBackgroundAttachment: z.enum(["scroll", "fixed"]).optional().nullable(),
  sectionBackgroundRepeat: z.enum(["no-repeat", "repeat", "repeat-x", "repeat-y"]).optional().nullable(),
  sectionScrollEffect: z.enum(["none", "parallax", "fade-in", "slide-up"]).optional().nullable(),
  sectionIdleAnimation: z.enum(["none", "ambient-float", "subtle-pulse"]).optional().nullable(),

  // ── Per-Section Color Overrides ──
  sectionTextColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional().nullable(),
  sectionHeadingColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional().nullable(),
  sectionMutedColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional().nullable(),
  sectionAccentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional().nullable(),
  sectionCardBg: z.string().max(40).optional().nullable(),
  sectionCardBorder: z.string().max(40).optional().nullable(),

  // ── Section Sizing & Column Grids ──
  sectionWidth: z.enum(["default", "narrow", "expanded", "full-bleed"]).optional().nullable(),
  itemColumns: z.enum(["1", "2", "3", "auto-fit"]).optional().nullable(),

  // ── Companion Media & Rich Embeds ──
  mediaType: z.enum(["none", "image", "image-gallery", "carousel", "video", "animation"]).optional().nullable(),
  mediaPosition: z.enum(["split-right", "split-left", "top", "bottom"]).optional().nullable(),
  mediaSplitRatio: z.enum(["50-50", "60-40", "70-30", "40-60", "30-70"]).optional().nullable(),
  mediaItems: z.array(z.object({
    url: z.string().max(1024),
    alt: z.string().max(200).optional(),
    caption: z.string().max(300).optional(),
    linkUrl: z.string().max(1024).optional(),
  })).max(10).optional().nullable(),
  mediaGalleryLayout: z.enum(["stacked", "grid", "row"]).optional().nullable(),
  mediaVideoUrl: z.string().max(1024).optional().nullable(),
  mediaAspectRatio: z.enum(["auto", "16-9", "4-3", "1-1", "3-4"]).optional().nullable(),
  mediaBorderRadius: z.enum(["none", "sm", "md", "lg", "full"]).optional().nullable(),
  mediaShadow: z.enum(["none", "subtle", "elevated", "3d", "glow"]).optional().nullable(),
  mediaBevel: z.enum(["none", "light", "medium", "high"]).optional().nullable(),
});
export type SectionOverride = z.infer<typeof sectionOverrideSchema>;

// ─── Hero Overrides (Pro) ────────────────────────────────────────────────────
export const heroOverridesSchema = z.object({
  // Avatar controls
  avatarVisible: z.boolean().optional(),
  avatarShape: z.enum(["circle", "rounded", "square", "hexagon", "squircle", "none"]).optional(),
  avatarSize: z.string().max(30).optional(),
  avatarBorder: z.enum(["none", "thin", "medium", "thick", "accent", "gradient", "dashed", "double"]).optional().nullable(),
  avatarBorderColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional().nullable(),
  avatarBorderGradient: z.enum(["none", "sunset", "aurora", "cyber", "gold", "primary-accent"]).optional().nullable(),
  avatarShadow: z.enum(["none", "subtle", "elevated", "3d", "glow"]).optional().nullable(),
  avatar3dEffect: z.boolean().optional().nullable(),
  avatarBevel: z.enum([
    "none",
    "light",
    "medium",
    "high",
    "soft",
    "chiseled",
    "glass-lens",
    "embossed",
  ]).optional().nullable(),
  avatarAnimation: z.enum(["none", "float", "pulse-glow", "spin-ring"]).optional().nullable(),
  avatarBgColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional().nullable(),
  avatarBgGradient: z.enum(["none", "glass", "subtle-light", "dark-slate", "accent-tint"]).optional().nullable(),
  avatarPadding: z.enum(["none", "sm", "md"]).optional().nullable(),
  avatarPosition: z.enum(["center", "left", "right"]).optional(),
  // Name & title controls
  customName: z.string().max(100).transform(v => sanitizeTitle(v)).optional().nullable(),
  nameVisible: z.boolean().optional(),
  nameSize: z.string().max(30).optional(),
  nameAlign: z.enum(["left", "center", "right"]).optional(),
  nameWeight: z.enum(["normal", "medium", "semibold", "bold", "extrabold", "black"]).optional().nullable(),
  nameStyle: z.enum(["default", "gradient", "italic", "uppercase", "underlined"]).optional().nullable(),
  nameColor: z.string().max(30).optional().nullable(),
  nameTracking: z.enum(["tight", "normal", "wide", "widest"]).optional().nullable(),
  titleVisible: z.boolean().optional(),
  bioVisible: z.boolean().optional(),
  // Hero layout
  heroLayout: z.enum(["standard", "split-left", "split-right", "banner", "minimal", "card"]).optional(),
  // Hero background
  heroBackgroundImageUrl: z.string().url().max(1024).optional().or(z.literal("")).nullable(),
  heroBackgroundOverlay: z.string().max(30).optional().nullable(), // hex with alpha e.g. #00000080
  heroBackgroundPosition: z.enum(["cover", "contain", "top", "center", "bottom", "fixed"]).optional().nullable(),
  heroPadding: z.enum(["none", "sm", "md", "lg", "xl"]).optional().nullable(),
});
export type HeroOverrides = z.infer<typeof heroOverridesSchema>;

export const pageBevelColorEnum = z.enum([
  "white",
  "black",
  "accent",
  "cyan",
  "gold",
  "purple",
]);
export type PageBevelColor = z.infer<typeof pageBevelColorEnum>;

export const themeEffectsSchema = z.object({
  level: z.enum(["none", "subtle", "expressive"]).optional(),
  glass: z.boolean().optional(),
  glow: z.boolean().optional(),
  hoverLift: z.boolean().optional(),
  ambientBackground: z.boolean().optional(),
  pageBevel: z.enum([
    "none",
    "light",
    "medium",
    "high",
    "subtle-rim",
    "curved-glass",
    "mobile-bezel",
  ]).optional().nullable(),
  pageBevelColor: pageBevelColorEnum.optional().nullable(),
}).optional();
export type ThemeEffectsConfig = z.infer<typeof themeEffectsSchema>;

export const storedLayoutOverridesSchema = z.object({
  contentMaxWidth: z.number().int().min(720).max(1280).optional(),
  showWatermark: z.boolean().optional(),
  customFooterText: z.string().max(200).optional(),
  sections: z.record(z.string(), sectionOverrideSchema).optional(),
  sectionOverridesById: z.record(z.string(), sectionOverrideSchema).optional(),
  hero: heroOverridesSchema.optional(),
  effects: themeEffectsSchema,
  pageBevel: z.enum([
    "none",
    "light",
    "medium",
    "high",
    "subtle-rim",
    "curved-glass",
    "mobile-bezel",
  ]).optional().nullable(),
  pageBevelColor: pageBevelColorEnum.optional().nullable(),
  _deprecated_proCustomCss: z.string().max(10000).optional(),
});
export type StoredLayoutOverrides = z.infer<typeof storedLayoutOverridesSchema>;

export const layoutOverridesSchema = storedLayoutOverridesSchema.extend({
  _deprecated_proCustomCss: z.never({
    invalid_type_error: "Legacy custom CSS cannot be modified via active updates.",
  }).optional(),
});
export type LayoutOverrides = z.infer<typeof layoutOverridesSchema>;

export const customColorsSchema = z.object({
  primary: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional(),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional(),
  background: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional(),
  foreground: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color").optional(),
}).nullable();
export type CustomColors = z.infer<typeof customColorsSchema>;

export const seoOverridesSchema = z.object({
  ogTitle: z.string().max(120).optional(),
  ogDescription: z.string().max(300).optional(),
  ogImageUrl: z.string().url().max(1024).optional(),
});
export type SeoOverrides = z.infer<typeof seoOverridesSchema>;

export const storedProfileSchema = z.object({
  fullName: z.string().min(2).max(255).transform(v => sanitizeTitle(v)),
  title: z.string().max(255).transform(v => sanitizeTitle(v)).optional().nullable(),
  bio: z.string().max(5000).optional().nullable(),
  photoUrl: safeHttpUrlSchema.optional().nullable(),
  language: z.enum(["en", "si", "ta"]).default("en"),
  colorPalette: colorPalettesEnum.default("ocean-blue"),
  themeKey: themesEnum.default("classic-academic"),
  fontPairing: fontPairingsEnum.default("serif-classic"),
  backgroundStyle: backgroundStyleEnum.default("solid"),
  headerStyle: headerStyleEnum.default("standard"),
  animationLevel: animationLevelEnum.default("subtle"),
  layoutDensity: layoutDensityEnum.default("standard"),
  cornerRadius: cornerRadiusEnum.default("subtle"),
  sectionArrangement: sectionArrangementEnum.default("linear"),
  typographyScale: typographyScaleEnum.default("editorial"),
  styleRecipe: styleRecipesEnum.optional().nullable(),
  layoutOverrides: storedLayoutOverridesSchema.optional().default({}),
  customColors: customColorsSchema.optional().nullable().default(null),
  seoOverrides: seoOverridesSchema.optional().default({}),
});

export const updateDesignSchema = z.object({
  colorPalette: colorPalettesEnum,
  themeKey: themesEnum,
  fontPairing: fontPairingsEnum.default("serif-classic"),
  backgroundStyle: backgroundStyleEnum.default("solid"),
  headerStyle: headerStyleEnum.default("standard"),
  animationLevel: animationLevelEnum.default("subtle"),
  layoutDensity: layoutDensityEnum.default("standard"),
  cornerRadius: cornerRadiusEnum.default("subtle"),
  sectionArrangement: sectionArrangementEnum.default("linear"),
  typographyScale: typographyScaleEnum.default("editorial"),
  styleRecipe: styleRecipesEnum.optional().nullable(),
  layoutOverrides: layoutOverridesSchema.optional(),
  customColors: customColorsSchema.optional().nullable(),
  seoOverrides: seoOverridesSchema.optional(),
});
export const themeCustomizationSchema = updateDesignSchema;

export const updateSubdomainSchema = z.object({
  subdomain: subdomainSchema,
});

// ─── Modular Section Validators ──────────────────────────────────────────────

export * from "./safe-url";
export * from "./sections/about";
export * from "./sections/publications";
export * from "./sections/research";
export * from "./sections/experience";
export * from "./sections/contact";
export * from "./sections/teaching";
export * from "./sections/projects";
export * from "./sections/skills";
export * from "./sections/downloads";
export * from "./sections/media";

import { aboutContentSchema } from "./sections/about";
import { publicationsContentSchema } from "./sections/publications";
import { researchContentSchema } from "./sections/research";
import { experienceContentSchema } from "./sections/experience";
import { contactContentSchema, draftContactContentSchema } from "./sections/contact";
import { teachingContentSchema, draftTeachingContentSchema } from "./sections/teaching";
import { projectsContentSchema, draftProjectsContentSchema } from "./sections/projects";
import { skillsContentSchema, draftSkillsContentSchema } from "./sections/skills";
import { downloadsContentSchema } from "./sections/downloads";
import { mediaContentSchema } from "./sections/media";
export * from "./sections/media";

// Map all 10 section types to their content validators (strict publish mode)
export const sectionContentValidators = {
  about: aboutContentSchema,
  publications: publicationsContentSchema,
  experience: experienceContentSchema,
  research: researchContentSchema,
  teaching: teachingContentSchema,
  projects: projectsContentSchema,
  skills: skillsContentSchema,
  contact: contactContentSchema,
  downloads: downloadsContentSchema,
  media: mediaContentSchema,
} as const;

// Map all 10 section types to their draft content validators (permissive draft mode)
export const sectionDraftContentValidators = {
  about: aboutContentSchema,
  publications: publicationsContentSchema,
  experience: experienceContentSchema,
  research: researchContentSchema,
  teaching: draftTeachingContentSchema,
  projects: draftProjectsContentSchema,
  skills: draftSkillsContentSchema,
  contact: draftContactContentSchema,
  downloads: downloadsContentSchema,
  media: mediaContentSchema,
} as const;

export type SectionType = keyof typeof sectionContentValidators;

/**
 * Validates polymorphic section content safely against the strict schema for publishing.
 */
export function validateSectionContent(type: SectionType, content: unknown) {
  const validator = sectionContentValidators[type];
  if (!validator) {
    return {
      success: false as const,
      error: new Error(`Unknown section type: ${type}`),
    };
  }
  return validator.safeParse(content);
}

/**
 * Validates polymorphic section content safely against the permissive draft schema for editing/autosaving.
 */
export function validateSectionDraftContent(type: SectionType, content: unknown) {
  const validator = sectionDraftContentValidators[type];
  if (!validator) {
    return {
      success: false as const,
      error: new Error(`Unknown section type: ${type}`),
    };
  }
  return validator.safeParse(content);
}

export const polymorphicSectionContentDraftSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("about"), content: aboutContentSchema }),
  z.object({ type: z.literal("research"), content: researchContentSchema }),
  z.object({ type: z.literal("teaching"), content: draftTeachingContentSchema }),
  z.object({ type: z.literal("projects"), content: draftProjectsContentSchema }),
  z.object({ type: z.literal("publications"), content: publicationsContentSchema }),
  z.object({ type: z.literal("experience"), content: experienceContentSchema }),
  z.object({ type: z.literal("contact"), content: draftContactContentSchema }),
  z.object({ type: z.literal("skills"), content: draftSkillsContentSchema }),
  z.object({ type: z.literal("downloads"), content: downloadsContentSchema }),
  z.object({ type: z.literal("media"), content: mediaContentSchema }),
]);

export const polymorphicSectionContentPublishSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("about"), content: aboutContentSchema }),
  z.object({ type: z.literal("research"), content: researchContentSchema }),
  z.object({ type: z.literal("teaching"), content: teachingContentSchema }),
  z.object({ type: z.literal("projects"), content: projectsContentSchema }),
  z.object({ type: z.literal("publications"), content: publicationsContentSchema }),
  z.object({ type: z.literal("experience"), content: experienceContentSchema }),
  z.object({ type: z.literal("contact"), content: contactContentSchema }),
  z.object({ type: z.literal("skills"), content: skillsContentSchema }),
  z.object({ type: z.literal("downloads"), content: downloadsContentSchema }),
  z.object({ type: z.literal("media"), content: mediaContentSchema }),
]);

// Backwards-compatible draft validation alias
export const polymorphicSectionContentSchema = polymorphicSectionContentDraftSchema;

// ─── Section CRUD Validators ─────────────────────────────────────────────────

export const createSectionSchema = z.object({
  type: sectionTypeEnum,
  pageId: z.string().uuid().optional(),
  visible: z.boolean().default(true),
  displayOrder: z.number().int().min(0).optional(),
  content: z.record(z.unknown()),
});

export const updateSectionSchema = z.object({
  sectionId: z.string().uuid(),
  pageId: z.string().uuid().optional(),
  type: sectionTypeEnum.optional(),
  visible: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
  content: z.record(z.unknown()),
});

export const upsertSectionSchema = z.object({
  sectionId: z.string().uuid().optional(),
  pageId: z.string().uuid().optional(),
  type: sectionTypeEnum,
  visible: z.boolean().optional().default(true),
  displayOrder: z.number().int().min(0).optional(),
  content: z.record(z.unknown()),
});

export const deleteSectionSchema = z.object({
  sectionId: z.string().uuid(),
});

export const toggleSectionVisibilitySchema = z.object({
  sectionId: z.string().uuid(),
  visible: z.boolean(),
});

export const reorderSectionsSchema = z.object({
  sections: z.array(
    z.object({
      id: z.string().uuid(),
      displayOrder: z.number().int().min(0),
    })
  ),
});

// ─── Media Validators ───────────────────────────────────────────────────────

export const getUploadUrlSchema = z.object({
  fileName: z.string().min(1).max(255),
  fileType: z.string().min(1).max(100),
  fileSize: z.number().int().positive().max(25 * 1024 * 1024), // 25MB max
  category: z.enum(["avatar", "figure", "document", "other"]).default("avatar"),
});

export const confirmUploadSchema = z.object({
  fileName: z.string().min(1).max(255),
  fileType: z.string().min(1).max(100),
  fileSize: z.number().int().positive(),
  category: z.enum(["avatar", "figure", "document", "other"]).default("avatar"),
  objectKey: z.string().min(1),
});

export const deleteMediaSchema = z.object({
  mediaId: z.string().uuid().optional(),
  url: z.string().optional(),
  objectKey: z.string().optional(),
});
export type DeleteMediaInput = z.infer<typeof deleteMediaSchema>;

// ─── CV Validators ──────────────────────────────────────────────────────────

export const getCVUploadUrlSchema = z.object({
  includedSections: z.array(z.string()).min(1),
  template: z.string().default("standard"),
});

export const confirmCVUploadSchema = z.object({
  shareToken: z.string().uuid(),
});

// ─── Page CRUD Validators ───────────────────────────────────────────────────

export const RESERVED_PAGE_SLUGS = new Set([
  "admin",
  "api",
  "assets",
  "auth",
  "billing",
  "cv",
  "dashboard",
  "login",
  "preview",
  "register",
  "settings",
  "signin",
  "signup",
  "p",
  "static",
  "_next",
  "favicon.ico",
  "robots.txt",
  "sitemap.xml",
]);

export function isReservedPageSlug(slug: string): boolean {
  if (!slug) return false;
  const normalized = slug.trim().toLowerCase();
  return RESERVED_PAGE_SLUGS.has(normalized);
}

export function normalizePageSlug(slug: string): string {
  return slug
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export const createPageSchema = z.object({
  title: z.string().min(1, "Page title is required").max(255),
  navigationLabel: z.string().max(100).optional().nullable(),
  description: z.string().optional().nullable(),
  slug: z
    .string()
    .min(1, "Page slug is required")
    .max(100)
    .transform(normalizePageSlug)
    .refine((s) => s.length > 0 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s), {
      message: "Slug must contain only lowercase letters, numbers, and hyphens",
    })
    .refine((s) => !isReservedPageSlug(s), {
      message: "This slug is reserved by the platform",
    }),
  seoTitle: z.string().max(255).optional().nullable(),
  seoDescription: z.string().optional().nullable(),
  visible: z.boolean().optional().default(true),
  initialSections: z
    .array(
      z.object({
        type: sectionTypeEnum,
        content: z.record(z.unknown()).optional().default({}),
        visible: z.boolean().optional().default(true),
        displayOrder: z.number().int().optional(),
      })
    )
    .optional(),
});

export const updatePageSchema = z.object({
  pageId: z.string().uuid(),
  title: z.string().min(1, "Page title is required").max(255).optional(),
  navigationLabel: z.string().max(100).optional().nullable(),
  description: z.string().optional().nullable(),
  slug: z
    .string()
    .max(100)
    .optional()
    .transform((val) => (val !== undefined ? normalizePageSlug(val) : undefined))
    .refine((s) => s === undefined || s === "" || (/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s) && !isReservedPageSlug(s)), {
      message: "Invalid or reserved page slug",
    }),
  seoTitle: z.string().max(255).optional().nullable(),
  seoDescription: z.string().optional().nullable(),
  visible: z.boolean().optional(),
});

export const deletePageSchema = z.object({
  pageId: z.string().uuid(),
});

export const reorderPagesSchema = z.object({
  pageIds: z.array(z.string().uuid()).min(1),
});

export * from "./tiers";
export * from "./display-options";
export * from "./billing";
